import { useEffect, useState, useMemo } from "react";
import {
  Star, Eye, EyeOff, Loader2, Pin, PinOff, RefreshCw, ExternalLink,
  MessageSquare, AlertTriangle, ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3 h-3 ${s <= value ? "fill-[#9A7B4F] text-[#9A7B4F]" : "text-[#282828]/10"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function AdminBusinessReviews() {
  const [status, setStatus] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/business-reviews/status`, { headers }),
        axios.get(`${API}/admin/business-reviews`, { headers }),
      ]);
      setStatus(statusRes.data);
      setReviews(listRes.data.reviews || []);
      setMeta(listRes.data.meta || {});
    } catch (e) {
      toast.error("Failed to load Google reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.post(`${API}/admin/business-reviews/refresh`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Fetched ${res.data.meta?.last_fetch_count ?? 0} review(s) from Google`);
      await fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail || "Refresh failed";
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePatch = async (id, body) => {
    setActionId(id);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.patch(`${API}/admin/business-reviews/${id}`, body, { headers: { Authorization: `Bearer ${token}` } });
      setReviews((prev) => prev.map((r) => (r.id === id ? res.data.review : r)));
      toast.success("Review updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setActionId(null);
    }
  };

  const filtered = useMemo(() => {
    return (reviews || []).filter((r) => {
      if (filter === "visible") return r.hidden !== true;
      if (filter === "hidden") return r.hidden === true;
      if (filter === "featured") return r.featured === true;
      return true;
    });
  }, [reviews, filter]);

  const counts = useMemo(() => ({
    all: reviews.length,
    visible: reviews.filter((r) => !r.hidden).length,
    hidden: reviews.filter((r) => r.hidden).length,
    featured: reviews.filter((r) => r.featured).length,
  }), [reviews]);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-[900px] mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1
                className="text-2xl text-[#282828]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                data-testid="admin-business-reviews-title"
              >
                Business Reviews (Google)
              </h1>
              <p className="text-[12px] text-[#676664]/50 mt-1">
                {meta.rating ? `${meta.rating.toFixed(1)} ★ on Google · ${meta.user_ratings_total || 0} total reviews` : "Synced from your Google Business Profile"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {meta.place_url && (
                <a
                  href={meta.place_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] text-[#676664] hover:text-[#9A7B4F] border border-[#282828]/12 px-3 py-2 transition-colors"
                  data-testid="open-on-google"
                >
                  Open on Google <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <Button
                onClick={handleRefresh}
                disabled={refreshing || !(status?.configured)}
                className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase"
                data-testid="refresh-google-reviews-btn"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1.5" /> Refresh from Google</>}
              </Button>
            </div>
          </div>

          {/* Config banner */}
          {!loading && status && !status.configured && (
            <div className="mb-5 border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="config-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-[12px] text-amber-900 leading-relaxed">
                <p className="font-medium mb-1">Google Reviews integration not configured.</p>
                <p>
                  Set <code className="bg-white/60 px-1 rounded">GOOGLE_PLACES_SERVER_KEY</code>
                  {" "}and <code className="bg-white/60 px-1 rounded">GOOGLE_PLACES_PLACE_ID</code>
                  {" "}in backend <code>.env</code>, restart the backend, then click "Refresh from Google". See the Batch 8 integration guide for the Google Cloud API key setup.
                </p>
                {status.last_fetched_at && (
                  <p className="mt-1 text-amber-700/80">Last successful fetch: {new Date(status.last_fetched_at).toLocaleString("en-AU")}</p>
                )}
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="flex gap-1 mb-5 bg-white border border-[#282828]/8 p-1 w-fit" data-testid="biz-review-filter-tabs">
            {[
              { key: "all", label: `All (${counts.all})` },
              { key: "visible", label: `Visible (${counts.visible})` },
              { key: "featured", label: `Featured (${counts.featured})` },
              { key: "hidden", label: `Hidden (${counts.hidden})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.05em] uppercase transition-all ${filter === tab.key ? "bg-[#9A7B4F] text-white" : "text-[#676664]/60 hover:text-[#282828]"}`}
                data-testid={`biz-filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="w-10 h-10 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />
              <p className="text-[13px] text-[#676664]/40">
                {status?.configured ? "No reviews in this category." : 'Set up the API key, then click "Refresh from Google".'}
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="business-reviews-admin-list">
              {filtered.map((r) => {
                const date = r.publish_time ? new Date(r.publish_time).toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" }) : r.relative_time || "";
                const busy = actionId === r.id;
                return (
                  <div
                    key={r.id}
                    className={`bg-white border p-4 ${r.featured ? "border-[#9A7B4F]/40" : r.hidden ? "border-[#282828]/5 opacity-60" : "border-[#282828]/8"}`}
                    data-testid={`admin-business-review-${r.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-[13px] text-[#282828] font-medium">{r.author_name}</span>
                          <Stars value={r.rating || 0} />
                          <span className="text-[10px] text-[#676664]/35">{date}</span>
                          {r.featured && (
                            <span className="px-2 py-0.5 bg-[#9A7B4F]/10 text-[#9A7B4F] text-[9px] font-medium uppercase tracking-wider rounded">
                              Featured
                            </span>
                          )}
                          {r.hidden && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[9px] font-medium uppercase tracking-wider rounded">
                              Hidden
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[9px] font-medium uppercase tracking-wider rounded">Google</span>
                        </div>
                        {r.text && (
                          <p className="text-[13px] text-[#5A5A5A] leading-relaxed mt-2 whitespace-pre-line">{r.text}</p>
                        )}
                        <p className="text-[10px] text-[#676664]/30 mt-1.5">
                          Order: {r.display_order || 0}
                          {r.updated_by ? ` · Last updated by ${r.updated_by}` : ""}
                          {r.last_seen_at ? ` · Last seen on Google ${new Date(r.last_seen_at).toLocaleDateString("en-AU")}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {busy ? (
                          <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" />
                        ) : (
                          <>
                            <button
                              onClick={() => handlePatch(r.id, { display_order: (r.display_order || 0) + 1 })}
                              className="p-1.5 text-[#676664]/30 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/8 rounded"
                              title="Move up in display order"
                              data-testid={`order-up-${r.id}`}
                            >
                              <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => handlePatch(r.id, { display_order: (r.display_order || 0) - 1 })}
                              className="p-1.5 text-[#676664]/30 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/8 rounded"
                              title="Move down in display order"
                              data-testid={`order-down-${r.id}`}
                            >
                              <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            {r.featured ? (
                              <button
                                onClick={() => handlePatch(r.id, { featured: false })}
                                className="p-1.5 text-[#9A7B4F] hover:bg-[#9A7B4F]/10 rounded"
                                title="Unfeature"
                                data-testid={`unfeature-${r.id}`}
                              >
                                <PinOff className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePatch(r.id, { featured: true })}
                                className="p-1.5 text-[#676664]/30 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/10 rounded"
                                title="Feature / pin this review"
                                data-testid={`feature-${r.id}`}
                              >
                                <Pin className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            )}
                            {r.hidden ? (
                              <button
                                onClick={() => handlePatch(r.id, { hidden: false })}
                                className="p-1.5 text-[#676664]/30 hover:text-green-500 hover:bg-green-50 rounded"
                                title="Show on storefront"
                                data-testid={`show-${r.id}`}
                              >
                                <Eye className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePatch(r.id, { hidden: true })}
                                className="p-1.5 text-[#676664]/30 hover:text-red-400 hover:bg-red-50 rounded"
                                title="Hide from storefront"
                                data-testid={`hide-${r.id}`}
                              >
                                <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footnote — review text immutability */}
          <p className="mt-6 text-[10px] text-[#676664]/40 leading-relaxed">
            Google's terms require that review text and reviewer names are displayed verbatim. The
            buttons above only control visibility, pin/feature, and display order — they do not edit
            the review content itself.
          </p>
        </div>
      </main>
    </div>
  );
}
